/**
 * What the rider's phone reported for a door, said in words beside what the ERP booked (migration 219; the owner,
 * 2026-10-01: "Dilawar delivered the bottle, scanned and verified, and it is not shown here"). A report the ERP holds is
 * the rider's word, not yet a booking: it says what was handed over and why it waits — most often that the round
 * carries no stock yet, so the ledger has no lot to take the bottles from.
 */
import { Tag } from './Tag';
import { time as fmtTime } from '../../ui/format';

export interface PhoneReport {
  event: 'delivery_completed' | 'delivery_failed'; at: string; receivedAt: string; outcome: 'ACCEPTED' | 'HELD' | 'DISCARDED';
  heldCode: string | null; heldDetail: string | null; units: number; shortReason: string | null; reason: string | null;
}

const HELD_WORDS: Record<string, string> = {
  HELD_NOT_LOADED: 'waits for stock: the round carries no lots yet, so nothing can be booked out',
  HELD_EXTRA_UNKNOWN_BOTTLE: 'waits: an extra bottle the ERP does not know',
  HELD_EXTRA_VARIANT_UNKNOWN: 'waits: an extra bottle with no known fill',
  HELD_NOT_YET_APPLIED: 'received; its effect is not wired yet',
  HELD_REFUSED_ON_APPLY: 'refused when it was applied — the office decides',
};
export const heldWords = (code: string | null) => (code ? HELD_WORDS[code] ?? code.toLowerCase().replace(/_/g, ' ') : '');
const time = (iso: string) => fmtTime(iso);

/** The phone's word, as a tag: "Delivered on the phone · 2 · 07:39", amber while the ERP holds it. */
export function PhoneSaid({ report }: { report: PhoneReport | null | undefined }) {
  if (!report) return <span className="text-ink-faint">nothing yet</span>;
  const delivered = report.event === 'delivery_completed';
  const words = `${delivered ? `Delivered on the phone · ${report.units}` : 'Not delivered (phone)'} · ${time(report.at)}`;
  if (report.outcome === 'HELD') {
    return <Tag tone="queued" title={report.heldDetail ?? undefined}>{words} · held</Tag>;
  }
  return <Tag tone={delivered ? 'accepted' : 'refused'}>{words}</Tag>;
}

export const doorWords = (door: string | null | undefined) =>
  !door ? '—' : door === 'qr' ? 'QR scanned' : door === 'qr_mismatch' ? 'QR did not match' : `scan skipped (${door.slice(8).toLowerCase().replace(/_/g, ' ')})`;
